import { useCallback, useEffect, useMemo, useState } from 'react'
import { unsourcedThoughts } from '../../lib/blocks'
import type { Block } from '../../lib/types'

// The publish sheet's "Fix" as a guided walk. It flags N thoughts without a
// source and opens the composer on the first; each Save then moves straight
// on to the next one still missing a source, until there are none. Skip
// passes one by for this walk; Done ends it early.

/**
 * The next thought still missing a source after `fromId`, in reading order,
 * wrapping round to the top. `settled` are ids to pass over (just sourced,
 * whose save hasn't reached `blocks` yet, or skipped).
 */
export function nextUnsourced(blocks: Block[], fromId: string | null, settled: ReadonlySet<string>): string | null {
  const open = unsourcedThoughts(blocks).filter((b) => b.id !== fromId && !settled.has(b.id))
  if (open.length === 0) return null
  const order = new Map(blocks.map((b, i) => [b.id, i]))
  const from = fromId ? (order.get(fromId) ?? -1) : -1
  return (open.find((b) => (order.get(b.id) ?? 0) > from) ?? open[0]).id
}

export interface SourceWalk {
  /** The walk is on (the bar shows). */
  active: boolean
  /** Ended because nothing is left: the bar offers to go back and publish. */
  done: boolean
  /** Thoughts still without a source, not counting skipped ones. */
  left: number
  skipped: number
  start(id: string): void
  /** The form for `id` was saved; `sourced` is false when it was saved empty. */
  saved(id: string, sourced: boolean): void
  skip(): void
  stop(): void
}

export function useSourceWalk(blocks: Block[], open: (id: string) => void, notify: (m: string) => void): SourceWalk {
  const [current, setCurrent] = useState<string | null>(null)
  const [active, setActive] = useState(false)
  const [done, setDone] = useState(false)
  const [skippedIds, setSkippedIds] = useState<ReadonlySet<string>>(() => new Set())

  const left = useMemo(() => unsourcedThoughts(blocks).filter((b) => !skippedIds.has(b.id)).length, [blocks, skippedIds])

  const start = useCallback(
    (id: string) => {
      setActive(true)
      setDone(false)
      setSkippedIds(new Set())
      setCurrent(id)
      open(id)
    },
    [open],
  )

  const advance = (fromId: string, settled: ReadonlySet<string>, skippedCount: number) => {
    const next = nextUnsourced(blocks, fromId, settled)
    if (next) {
      setCurrent(next)
      open(next)
      return
    }
    setCurrent(null)
    setDone(true)
    notify(skippedCount > 0 ? `That’s all of them, apart from ${skippedCount} you skipped` : 'Every thought has a source now')
  }

  const saved = (id: string, sourced: boolean) => {
    if (!active || done) return
    if (!sourced) return open(id) // saved empty: it still needs one, so stay on it
    advance(id, new Set([...skippedIds, id]), skippedIds.size)
  }

  const skip = () => {
    if (!active || !current) return
    const nextSkipped = new Set(skippedIds).add(current)
    setSkippedIds(nextSkipped)
    advance(current, nextSkipped, nextSkipped.size)
  }

  const stop = useCallback(() => {
    setActive(false)
    setDone(false)
    setCurrent(null)
    setSkippedIds(new Set())
  }, [])

  // Toasts rise above the walk's bar while it shows.
  useEffect(() => {
    if (!active) return
    document.body.style.setProperty('--wr-toast-lift', '64px')
    return () => void document.body.style.removeProperty('--wr-toast-lift')
  }, [active])

  return { active, done, left, skipped: skippedIds.size, start, saved, skip, stop }
}
